import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, Brain, Search, Bot, Video, CheckCircle, Star, ArrowRight, Globe } from 'lucide-react';
import './LandingPage.css';

const content = {
  en: {
    nav: {
      features: 'Features',
      pricing: 'Pricing',
      testimonials: 'Testimonials',
      login: 'Log In',
      getStarted: 'Get Started Free',
      dashboard: 'Go to Dashboard',
    },
    hero: {
      badge: '✨ AI-Powered Prompt Engineering',
      headline: 'Transform Your Prompts Into',
      headlineAccent: 'Professional Masterpieces',
      sub: 'Stop struggling with vague AI outputs. Promptify Master rewrites your rough ideas into powerful, precise prompts that get exceptional results — every time.',
      cta: 'Start for Free',
      ctaSub: 'No credit card required · 10 free prompts/day',
      secondary: 'See How It Works',
    },
    stats: [
      { value: '50,000+', label: 'Prompts Improved' },
      { value: '5', label: 'Prompt Types' },
      { value: '98%', label: 'Satisfaction Rate' },
      { value: '10x', label: 'Better Results' },
    ],
    features: {
      title: 'Five Powerful Prompt Types',
      sub: 'Choose the right tool for every AI task — from everyday writing to cutting-edge video generation.',
      items: [
        {
          icon: 'Zap',
          title: 'Standard Prompt',
          desc: 'Perfect for everyday tasks. Our AI restructures your ideas into clear, context-rich prompts that any AI model understands perfectly.',
          badge: 'Most Popular',
        },
        {
          icon: 'Brain',
          title: 'Reasoning Prompt',
          desc: 'Built for complex logic, analysis, and step-by-step problem solving. Optimized for advanced models like GPT-5 and o-series.',
          badge: 'Advanced',
        },
        {
          icon: 'Search',
          title: 'Deep Research Prompt',
          desc: 'Craft prompts engineered for thorough web-based research. Get comprehensive, well-sourced answers instead of generic summaries.',
          badge: 'Research',
        },
        {
          icon: 'Bot',
          title: 'Custom GPT / Agent Prompt',
          desc: 'Design complete system prompts for your own Custom GPTs, AI assistants, or autonomous agents with professional-grade instructions.',
          badge: 'Builder',
        },
        {
          icon: 'Video',
          title: 'VEO-3 Video Prompt',
          desc: "Create cinematic prompts for Google's VEO-3 video generator. Specify scenes, styles, lighting, and motion like a professional director.",
          badge: 'New',
        },
      ],
    },
    howItWorks: {
      title: 'From Rough to Ready in Seconds',
      sub: 'Three simple steps to dramatically better AI results.',
      steps: [
        { num: '01', title: 'Type Your Idea', desc: 'Enter your rough prompt — no matter how simple or messy. Just describe what you want.' },
        { num: '02', title: 'Choose a Prompt Type', desc: 'Select the type that fits your task: Standard, Reasoning, Research, Agent, or Video.' },
        { num: '03', title: 'Get Your Masterpiece', desc: 'Receive a professionally crafted prompt ready to copy and paste into any AI tool.' },
      ],
    },
    pricing: {
      title: 'Simple, Transparent Pricing',
      sub: 'Start free. Scale when you need more.',
      plans: [
        {
          name: 'Free',
          price: '$0',
          period: '/month',
          desc: 'Perfect for trying out Promptify Master',
          features: ['10 prompts per day', 'Standard & Reasoning prompts', 'Copy to clipboard', 'Basic history'],
          cta: 'Get Started Free',
          highlighted: false,
        },
        {
          name: 'Standard',
          price: '$9.99',
          period: '/month',
          desc: 'For creators and professionals who need more',
          features: ['50 prompts per day', 'All 5 prompt types', 'Full prompt history', 'Priority processing', 'Email support'],
          cta: 'Start Standard',
          highlighted: true,
          badge: 'Most Popular',
        },
        {
          name: 'Premium',
          price: '$29.99',
          period: '/month',
          desc: 'For power users and teams with high volume needs',
          features: ['Unlimited prompts', 'All 5 prompt types', 'Full prompt history', 'Priority processing', 'API access', 'Priority support'],
          cta: 'Go Premium',
          highlighted: false,
        },
      ],
    },
    testimonials: {
      title: 'What Our Users Say',
      sub: 'Join thousands of creators, developers, and professionals who upgraded their AI game.',
      items: [
        {
          name: 'Sarah Chen',
          role: 'Content Strategist',
          text: 'Promptify Master changed the way I work with AI tools. My outputs went from mediocre to genuinely impressive in one click. Worth every penny.',
          stars: 5,
        },
        {
          name: 'Marcus Rivera',
          role: 'Software Developer',
          text: 'The Custom GPT prompt type is a game changer. I use it to design system prompts for my agents and it saves me hours of back-and-forth.',
          stars: 5,
        },
        {
          name: 'Elena Vasquez',
          role: 'Video Producer',
          text: 'VEO-3 prompts from this tool are incredible. The level of cinematic detail it adds is something that would take me 30 minutes to write manually.',
          stars: 5,
        },
      ],
    },
    cta: {
      title: 'Ready to Master the Art of Prompting?',
      sub: 'Join thousands of professionals getting better AI results every day. Start free — no credit card needed.',
      btn: 'Start for Free Now',
      sub2: 'Upgrade or cancel anytime.',
    },
    footer: {
      tagline: 'Transform your prompts into professional masterpieces.',
      copyright: '© 2025 Promptify Master. All rights reserved.',
    },
  },

  es: {
    nav: {
      features: 'Funciones',
      pricing: 'Precios',
      testimonials: 'Testimonios',
      login: 'Iniciar Sesión',
      getStarted: 'Empezar Gratis',
      dashboard: 'Ir al Panel',
    },
    hero: {
      badge: '✨ Ingeniería de Prompts con IA',
      headline: 'Transforma Tus Prompts en',
      headlineAccent: 'Obras Maestras Profesionales',
      sub: 'Deja de luchar con resultados vagos de IA. Promptify Master reescribe tus ideas en prompts potentes y precisos que obtienen resultados excepcionales — siempre.',
      cta: 'Comenzar Gratis',
      ctaSub: 'Sin tarjeta de crédito · 10 prompts gratis al día',
      secondary: 'Cómo Funciona',
    },
    stats: [
      { value: '50,000+', label: 'Prompts Mejorados' },
      { value: '5', label: 'Tipos de Prompt' },
      { value: '98%', label: 'Satisfacción' },
      { value: '10x', label: 'Mejores Resultados' },
    ],
    features: {
      title: 'Cinco Tipos de Prompts Poderosos',
      sub: 'Elige la herramienta correcta para cada tarea de IA — desde escritura cotidiana hasta generación de video de última generación.',
      items: [
        {
          icon: 'Zap',
          title: 'Prompt Estándar',
          desc: 'Perfecto para tareas cotidianas. Nuestra IA reestructura tus ideas en prompts claros y detallados que cualquier modelo de IA entiende perfectamente.',
          badge: 'Más Popular',
        },
        {
          icon: 'Brain',
          title: 'Prompt de Razonamiento',
          desc: 'Diseñado para lógica compleja, análisis y resolución de problemas paso a paso. Optimizado para modelos avanzados como GPT-5.',
          badge: 'Avanzado',
        },
        {
          icon: 'Search',
          title: 'Prompt de Investigación Profunda',
          desc: 'Crea prompts diseñados para investigación web exhaustiva. Obtén respuestas completas y bien documentadas en lugar de resúmenes genéricos.',
          badge: 'Investigación',
        },
        {
          icon: 'Bot',
          title: 'Prompt de GPT / Agente Personalizado',
          desc: 'Diseña prompts de sistema completos para tus propios GPTs personalizados, asistentes de IA o agentes autónomos con instrucciones de nivel profesional.',
          badge: 'Creador',
        },
        {
          icon: 'Video',
          title: 'Prompt de Video VEO-3',
          desc: 'Crea prompts cinematográficos para el generador de video VEO-3 de Google. Especifica escenas, estilos, iluminación y movimiento como un director profesional.',
          badge: 'Nuevo',
        },
      ],
    },
    howItWorks: {
      title: 'De Borrador a Listo en Segundos',
      sub: 'Tres pasos simples para obtener resultados de IA dramáticamente mejores.',
      steps: [
        { num: '01', title: 'Escribe Tu Idea', desc: 'Ingresa tu prompt en bruto — sin importar cuán simple o desordenado esté. Solo describe lo que quieres.' },
        { num: '02', title: 'Elige un Tipo de Prompt', desc: 'Selecciona el tipo que se adapte a tu tarea: Estándar, Razonamiento, Investigación, Agente o Video.' },
        { num: '03', title: 'Obtén Tu Obra Maestra', desc: 'Recibe un prompt creado profesionalmente, listo para copiar y pegar en cualquier herramienta de IA.' },
      ],
    },
    pricing: {
      title: 'Precios Simples y Transparentes',
      sub: 'Comienza gratis. Escala cuando necesites más.',
      plans: [
        {
          name: 'Gratis',
          price: '$0',
          period: '/mes',
          desc: 'Perfecto para probar Promptify Master',
          features: ['10 prompts al día', 'Prompts Estándar y Razonamiento', 'Copiar al portapapeles', 'Historial básico'],
          cta: 'Empezar Gratis',
          highlighted: false,
        },
        {
          name: 'Estándar',
          price: '$9.99',
          period: '/mes',
          desc: 'Para creadores y profesionales que necesitan más',
          features: ['50 prompts al día', 'Los 5 tipos de prompts', 'Historial completo', 'Procesamiento prioritario', 'Soporte por email'],
          cta: 'Empezar Estándar',
          highlighted: true,
          badge: 'Más Popular',
        },
        {
          name: 'Premium',
          price: '$29.99',
          period: '/mes',
          desc: 'Para usuarios avanzados y equipos con alto volumen',
          features: ['Prompts ilimitados', 'Los 5 tipos de prompts', 'Historial completo', 'Procesamiento prioritario', 'Acceso API', 'Soporte prioritario'],
          cta: 'Ir a Premium',
          highlighted: false,
        },
      ],
    },
    testimonials: {
      title: 'Lo Que Dicen Nuestros Usuarios',
      sub: 'Únete a miles de creadores, desarrolladores y profesionales que mejoraron su uso de IA.',
      items: [
        {
          name: 'Sarah Chen',
          role: 'Estratega de Contenido',
          text: 'Promptify Master cambió la manera en que trabajo con herramientas de IA. Mis resultados pasaron de mediocres a genuinamente impresionantes en un clic. Vale cada centavo.',
          stars: 5,
        },
        {
          name: 'Marcus Rivera',
          role: 'Desarrollador de Software',
          text: 'El tipo de prompt para Custom GPT es revolucionario. Lo uso para diseñar prompts de sistema para mis agentes y me ahorra horas de prueba y error.',
          stars: 5,
        },
        {
          name: 'Elena Vasquez',
          role: 'Productora de Video',
          text: 'Los prompts de VEO-3 de esta herramienta son increíbles. El nivel de detalle cinematográfico que añade es algo que me tomaría 30 minutos escribir manualmente.',
          stars: 5,
        },
      ],
    },
    cta: {
      title: '¿Listo para Dominar el Arte de los Prompts?',
      sub: 'Únete a miles de profesionales que obtienen mejores resultados de IA cada día. Comienza gratis — sin tarjeta de crédito.',
      btn: 'Comenzar Gratis Ahora',
      sub2: 'Actualiza o cancela en cualquier momento.',
    },
    footer: {
      tagline: 'Transforma tus prompts en obras maestras profesionales.',
      copyright: '© 2025 Promptify Master. Todos los derechos reservados.',
    },
  },
};

const iconMap = { Zap, Brain, Search, Bot, Video };

export default function LandingPage({ user }) {
  const [lang, setLang] = useState('en');
  const navigate = useNavigate();
  const t = content[lang];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="lp-root">
      {/* Background orbs */}
      <div className="lp-orb lp-orb-1" />
      <div className="lp-orb lp-orb-2" />
      <div className="lp-orb lp-orb-3" />

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-logo">
            <Sparkles size={24} className="lp-logo-icon" />
            <span>Promptify Master</span>
          </div>
          <div className="lp-nav-links">
            <button onClick={() => scrollTo('features')}>{t.nav.features}</button>
            <button onClick={() => scrollTo('pricing')}>{t.nav.pricing}</button>
            <button onClick={() => scrollTo('testimonials')}>{t.nav.testimonials}</button>
          </div>
          <div className="lp-nav-actions">
            <button
              className="lp-lang-btn"
              onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
              title="Toggle language"
            >
              <Globe size={16} />
              {lang === 'en' ? 'ES' : 'EN'}
            </button>
            {user ? (
              <button className="lp-btn-primary" onClick={() => navigate('/dashboard')}>
                {t.nav.dashboard}
              </button>
            ) : (
              <>
                <button className="lp-btn-ghost" onClick={() => navigate('/auth')}>
                  {t.nav.login}
                </button>
                <button className="lp-btn-primary" onClick={() => navigate('/auth')}>
                  {t.nav.getStarted}
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-badge">{t.hero.badge}</div>
          <h1 className="lp-headline">
            {t.hero.headline}
            <br />
            <span className="lp-headline-accent">{t.hero.headlineAccent}</span>
          </h1>
          <p className="lp-hero-sub">{t.hero.sub}</p>
          <div className="lp-hero-actions">
            <button className="lp-cta-btn" onClick={() => navigate('/auth')}>
              {t.hero.cta} <ArrowRight size={18} />
            </button>
            <button className="lp-secondary-btn" onClick={() => scrollTo('how-it-works')}>
              {t.hero.secondary}
            </button>
          </div>
          <p className="lp-hero-note">{t.hero.ctaSub}</p>
        </div>

        {/* Hero visual */}
        <div className="lp-hero-visual">
          <div className="lp-prompt-card lp-prompt-before">
            <div className="lp-card-label">Before</div>
            <p className="lp-card-text lp-text-dim">"write a blog post about AI"</p>
          </div>
          <div className="lp-arrow-connector">
            <Sparkles size={28} className="lp-logo-icon" />
          </div>
          <div className="lp-prompt-card lp-prompt-after">
            <div className="lp-card-label lp-label-accent">After</div>
            <p className="lp-card-text">
              "Act as an expert tech writer. Create a comprehensive 1,200-word blog post titled
              'How AI Is Reshaping Modern Business in 2025'…"
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="lp-stats">
        <div className="lp-stats-inner">
          {t.stats.map((s, i) => (
            <div key={i} className="lp-stat">
              <div className="lp-stat-value">{s.value}</div>
              <div className="lp-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="lp-section">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <h2 className="lp-section-title">{t.features.title}</h2>
            <p className="lp-section-sub">{t.features.sub}</p>
          </div>
          <div className="lp-features-grid">
            {t.features.items.map((f, i) => {
              const Icon = iconMap[f.icon];
              return (
                <div key={i} className="lp-feature-card">
                  <div className="lp-feature-top">
                    <div className="lp-feature-icon">
                      <Icon size={22} />
                    </div>
                    <span className="lp-feature-badge">{f.badge}</span>
                  </div>
                  <h3 className="lp-feature-title">{f.title}</h3>
                  <p className="lp-feature-desc">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="lp-section lp-how-section">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <h2 className="lp-section-title">{t.howItWorks.title}</h2>
            <p className="lp-section-sub">{t.howItWorks.sub}</p>
          </div>
          <div className="lp-steps">
            {t.howItWorks.steps.map((s, i) => (
              <div key={i} className="lp-step">
                <div className="lp-step-num">{s.num}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="lp-section">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <h2 className="lp-section-title">{t.pricing.title}</h2>
            <p className="lp-section-sub">{t.pricing.sub}</p>
          </div>
          <div className="lp-pricing-grid">
            {t.pricing.plans.map((plan, i) => (
              <div key={i} className={`lp-plan-card ${plan.highlighted ? 'lp-plan-highlighted' : ''}`}>
                {plan.badge && <div className="lp-plan-badge">{plan.badge}</div>}
                <h3 className="lp-plan-name">{plan.name}</h3>
                <div className="lp-plan-price">
                  <span className="lp-price-value">{plan.price}</span>
                  <span className="lp-price-period">{plan.period}</span>
                </div>
                <p className="lp-plan-desc">{plan.desc}</p>
                <ul className="lp-plan-features">
                  {plan.features.map((feat, j) => (
                    <li key={j} className="lp-plan-feature">
                      <CheckCircle size={16} className="lp-check-icon" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <button
                  className={plan.highlighted ? 'lp-cta-btn lp-plan-cta' : 'lp-btn-ghost lp-plan-cta'}
                  onClick={() => navigate('/auth')}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="lp-section lp-how-section">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <h2 className="lp-section-title">{t.testimonials.title}</h2>
            <p className="lp-section-sub">{t.testimonials.sub}</p>
          </div>
          <div className="lp-testimonials-grid">
            {t.testimonials.items.map((t2, i) => (
              <div key={i} className="lp-testimonial-card">
                <div className="lp-stars">
                  {Array.from({ length: t2.stars }).map((_, s) => (
                    <Star key={s} size={14} className="lp-star" fill="currentColor" />
                  ))}
                </div>
                <p className="lp-testimonial-text">"{t2.text}"</p>
                <div className="lp-testimonial-author">
                  <div className="lp-author-avatar">{t2.name.charAt(0)}</div>
                  <div>
                    <div className="lp-author-name">{t2.name}</div>
                    <div className="lp-author-role">{t2.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lp-final-cta">
        <div className="lp-final-cta-inner">
          <h2 className="lp-final-title">{t.cta.title}</h2>
          <p className="lp-final-sub">{t.cta.sub}</p>
          <button className="lp-cta-btn lp-cta-lg" onClick={() => navigate('/auth')}>
            {t.cta.btn} <ArrowRight size={20} />
          </button>
          <p className="lp-final-note">{t.cta.sub2}</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-logo">
            <Sparkles size={20} className="lp-logo-icon" />
            <span>Promptify Master</span>
          </div>
          <p className="lp-footer-tagline">{t.footer.tagline}</p>
          <p className="lp-footer-copy">{t.footer.copyright}</p>
        </div>
      </footer>
    </div>
  );
}
